import { useState } from 'react';
import { Box, Button, FormControlLabel, Switch } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SubjectList } from './SubjectList';
import { SubjectFormDialog } from './SubjectFormDialog';
import { SearchBar, SortMenu } from '../shared';
import type { SortConfig } from '../shared';
import type { Subject } from '@timetable/types';
import { useDebounce } from '../../hooks/useDebounce';

const SORT_OPTIONS = [
  { id: 'name', label: 'Name', field: 'subject_name' },
  { id: 'code', label: 'Code', field: 'subject_code' },
];

export function SubjectsTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'subject_name',
    direction: 'asc',
  });
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleAddClick = () => {
    setSelectedSubject(null);
    setFormOpen(true);
  };

  const handleEditClick = (subject: Subject) => {
    setSelectedSubject(subject);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedSubject(null);
  };

  return (
    <Box>
      {/* Search and Actions Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search subjects by name or code..."
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              size="small"
            />
          }
          label="Show inactive"
        />
        <SortMenu
          options={SORT_OPTIONS}
          value={sortConfig}
          onChange={setSortConfig}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Subject
        </Button>
      </Box>

      {/* Subject List */}
      <SubjectList
        searchQuery={debouncedSearchQuery}
        sortConfig={sortConfig}
        showInactive={showInactive}
        onEditClick={handleEditClick}
      />

      {/* Form Dialog */}
      <SubjectFormDialog
        open={formOpen}
        subject={selectedSubject}
        onClose={handleFormClose}
      />
    </Box>
  );
}
